import React, { useEffect, useState, useRef } from "react";
import { Dashboard } from "@uppy/react";
import { useTranslation } from "react-i18next";
import "@uppy/core/dist/style.css";
import "@uppy/dashboard/dist/style.css";
import "../../styles/uploader/uploader.css";
import DashboardSidebar from "../DashboardSideBar.jsx";
import Header from "../Header.jsx";
import Loading from "../Loading.jsx";
import { useAuth } from "../context/AuthContext.jsx";
import { fetchUploadedFiles } from "./DocsManagement";
import { createUppy } from "../../utils/uppyInstance.js";

export default function UppyUpload() {
  const { t } = useTranslation();
  const [uploadedFiles, setUploadedFilesState] = useState([]);
  const { user, token, loading } = useAuth();
  const username = user?.userName;
  const [filesLoading, setFilesLoading] = useState(false);
  const tusApi = process.env.REACT_APP_TUS_SERVER;
  const uppyRef = useRef(null);
  const [showMenu, setShowMenu] = useState(false);

  const toggleMenu = () => {
    setShowMenu(!showMenu);
  };
  const loadFiles = async () => {
    try {
      const files = await fetchUploadedFiles(tusApi, username, token);
      setUploadedFilesState([...files])
    } catch (err) {
      console.error("Failed to refresh uploaded files:", err);
    }
  };
  useEffect(() => {
    if (!username || !token) return;
    loadFiles();
    // If Uppy instance already exists, just reload files and exit
    if (uppyRef.current) {
      return;
    }

    const uppy = createUppy(token, username);
    uppyRef.current = uppy;



    uppy.on("complete", async (result) => {
      // console.log('completed')
      try {
        // Prepare the file stats array from successful uploads
        let files = result.successful.map(file => {
          // Extract fileId from Tus upload URL
          const uploadUrl = file.uploadURL || file.tus?.uploadUrl;
          const fileId = uploadUrl.split("/").pop();

          return {
            fileId,
            username: file.meta.username || "Unknown",
            fileName: file.name,
            completed: true,
            fileSize: file.size,
            fileDate: new Date().toISOString().split('T')[0]
          };
        });

        if (files.length === 0) return;

        // Call the incremental update endpoint
        const res = await fetch(`${tusApi}/api/uploads/update-filestats`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}` // if required
          },
          body: JSON.stringify({ files })
        });

        if (!res.ok) throw new Error("Failed to update file stats in Mongo");

        const data = await res.json();


        // Wait a tiny bit to ensure Mongo has completed all upserts

        // Refresh the frontend file list
        files = await loadFiles();
      } catch (err) {
        console.error("[Sync] Error updating file stats:", err);
      }
    });

    uppy.on('upload-progress', async (file, progress) => {
      try {
        const uploadUrl = file.uploadURL || file.tus?.uploadUrl;
        if (!uploadUrl) return; // skip if no Tus URL

        const fileId = uploadUrl.split("/").pop();

        // Prepare a minimal record for MongoDB
        const fileRecord = {
          fileId,
          username: file.meta.username || "Unknown",
          fileName: file.name,
          fileSize: file.size,
          fileDate: new Date().toISOString().split('T')[0],
          completed: false,    // mark as NOT completed yet
          infected: false,
          quarantine: true     // waiting for scan
        };

        // Update DB with upsert
        await fetch(`${tusApi}/api/uploads/update-filestats`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify({ files: [fileRecord] })
        });

      } catch (err) {
        console.error("[Upload-progress] Error updating file stats:", err);
      }
    });

    uppy.on("file-removed", async (file) => {
      // console.log('file removed trigger', file)
      uppy.getPlugin('Tus')?.resetUploader?.(file)
      const uploadUrl = file.tus?.uploadUrl;
      if (!uploadUrl) {
        console.warn(`[Delete+Sync] ${file.name} has no upload URL, skipping delete.`);
        return;
      }
      const fileId = file.tus.uploadUrl?.split("/").pop();
      try {
        await fetch(`${tusApi}/api/delete/files/${username}/${fileId}`, {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
        });

        // console.log(`[Delete+Sync] ${file.name} removed from R2 and Mongo, sync triggered`);

        // Optionally reload file list in UI
        await loadFiles();
      } catch (err) {
        console.error("[Delete+Sync] Error:", err);
      }
    });

    const handleBeforeUnload = (event) => {
      const ongoingUploads = uppy.getFiles().some(
        (file) => file.progress && !file.progress.uploadComplete
      );
      if (ongoingUploads) {
        event.preventDefault();
        event.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      // Don't destroy uppy if you want persistence
    };
  }, [username, token]);

  const totalSizeBytes = uploadedFiles.reduce((sum, file) => sum + file.fileSize, 0);
  const totalSizeMB = totalSizeBytes / (1024 * 1024);
  const maxSize = 10;

  if (loading || filesLoading) return <Loading />;


  return (
    <div className="uploadpage">
      <Header hideHamburger={false} profileHide={true} hideNav={true} toggleMenu={toggleMenu} />
      <div className="dashboard">
        <DashboardSidebar setShowMenu={setShowMenu} uploadedFiles={uploadedFiles} totalSizeMB={totalSizeMB} maxSize={maxSize} showMenu={showMenu} />
        <section className="uploader-container">
          <div className="header-upload">
            <h2>{t("add-doc")}</h2>
            <p>{t("add-doc-description")}</p>
          </div>
          <div className="uploader">
            {uppyRef.current && (
              <div className="dashboard-wrapper">
                <Dashboard
                  uppy={uppyRef.current}
                  inline={true}
                  showProgressDetails
                  showUploadButton={true}
                  proudlyDisplayPoweredByUppy={false}
                  className="custom-uppy-dashboard"
                  locale={{
                    strings: {
                      addMoreFiles: "Upload More Documents",
                      myDevice: "Local Computer",
                      browseFiles: t('browse-file'),
                      dropPasteFiles: t('drop-file'),
                      cancel: t('abort'),
                      saveChanges: t('apply change'),
                      addMore: t('add-more'),
                      back: t('back'),
                      recoveredAllFiles: t("recoveredAllFiles"),
                      sessionRestored: t("sessionRestored"),
                      reSelect: t("reSelect"),
                      dropHint: t("dropHint"),
                      xFilesSelected: { 0: t("xFilesSelected_0"), 1: t("xFilesSelected_1") },
                      recoveredXFiles: { 0: t("recoveredXFiles_0"), 1: t("recoveredXFiles_1") },
                      uploadingXFiles: { 0: t("uploadingXFiles_0"), 1: t("uploadingXFiles_1") },
                      processingXFiles: { 0: t("processingXFiles_0"), 1: t("processingXFiles_1") },
                      upload: t("upload"),
                      uploadPaused: t("uploadPaused"),
                      uploadXFiles: t("uploadXFiles"),
                      maxNumberOfFilesExceeded: t("maxNumberOfFilesExceeded"),
                      youCanOnlyUploadFileTypes: "hello"
                    },
                  }}
                />
              </div>
            )}
          </div>

        </section>
      </div>
    </div>
  );
}
